import { Dispatch, FormEvent, SetStateAction, useCallback, useContext, useEffect, useState } from "react";

import { BadgeTypeConstants, ProjectPermissionConstants, UserType } from "../../../../../constants";
import { BadgeData, BasicDetailsData, LocationData, ProjectDatesData, SelectData } from "../../../../../models";
import { updateProjectDetails, UpdateProjectDetailsRequest } from "../../../../../service/project";
import {
  GetProjectDetailsResponse,
  getStandardSpecificSchemaDetails,
  searchVerifiers,
} from "../../../../../service/query";
import { ServiceError, Status } from "../../../../../service/Shared";
import { useAuth } from "../../../../../useAuth";
import { isFalsyExceptZero, useIsLoadingWrapper } from "../../../../../utils";
import { dataGridMapFilterCriteria, Toast } from "../../../../../widget";
import { ProjectContext } from "../ProjectContext";
import { StandardSpecificDefinition } from "./components/StandardSpecificSection/types";

interface UseOverviewReturnData {
  currentUserType: UserType;
  projectDetails: GetProjectDetailsResponse | undefined;
  projectDates: ProjectDatesData | undefined;
  setProjectDates: Dispatch<SetStateAction<ProjectDatesData | undefined>>;
  currentStatus: string | undefined;
  errors: ServiceError[];
  isReadOnly: boolean;
  setIsReadOnly: (value: boolean) => void;
  isEditable: boolean;
  basicDetailsData: BasicDetailsData;
  setBasicDetailsData: Dispatch<SetStateAction<BasicDetailsData>>;
  locationData: LocationData;
  setLocationData: Dispatch<SetStateAction<LocationData>>;
  validatorData: SelectData;
  badgeData: BadgeData[];
  certificateData: BadgeData[];
  standardSpecificDefinition: StandardSpecificDefinition | undefined;
  onSubmit(e: FormEvent<HTMLFormElement> | undefined): Promise<void>;
  onCancel(): void;
  showUpdateGroupVVBModal: boolean;
  setShowUpdateGroupVVBModal: Dispatch<SetStateAction<boolean>>;
  showEditDatesPanel: boolean;
  setShowEditDatesPanel: Dispatch<SetStateAction<boolean>>;
  isOnSubmitLoading: boolean;
  shouldRefreshProjectDetails: () => void;
}

export const useOverview = (): UseOverviewReturnData => {
  const { currentUserType } = useAuth();
  const {
    projectDetails,
    setProjectDetails,
    projectDates,
    setProjectDates,
    hasProjectPermission,
    shouldRefreshProjectDetails,
  } = useContext(ProjectContext);
  const [isOnSubmitLoading, setIsOnSubmitLoading] = useState(false);
  const [standardSpecificDefinition, setStandardSpecificDefinition] = useState<
    StandardSpecificDefinition | undefined
  >();
  const [basicDetailsData, setBasicDetailsData] = useState<BasicDetailsData>({
    displayName: projectDetails?.displayName,
    name: projectDetails?.name,
    registryId: projectDetails?.projectReference?.externalReference,
    totalArea: projectDetails?.areaNetHa?.toString(),
    validatorUuid: projectDetails?.validator?.uuid,
    projectType: projectDetails?.projectType.displayName,
    projectStatus: projectDetails?.status,
    projectTypeUuid: projectDetails?.projectType.uuid,
  });

  const [locationData, setLocationData] = useState<LocationData>({
    addressCountryCode: projectDetails?.addressCountryCode,
    addressRegion: projectDetails?.addressRegion,
    gridReference: projectDetails?.locationGridReference,
    latitude: projectDetails?.locationCoordinates?.split(",")[0] || null,
    longitude: projectDetails?.locationCoordinates?.split(",")[1] || null,
    locationUuid: projectDetails?.projectLocation?.uuid || null,
    locationRowVersion: projectDetails?.projectLocation?.rowVersion || null,
    boundaryData: projectDetails?.projectLocation?.boundaryData || null,
  });

  const [submittedValues, setSubmittedValues] = useState<{
    basicDetailsData: BasicDetailsData;
    locationData: LocationData;
  }>({
    basicDetailsData: {
      displayName: projectDetails?.displayName,
      name: projectDetails?.name,
      registryId: projectDetails?.projectReference?.externalReference,
      totalArea: projectDetails?.areaNetHa?.toString(),
      validatorUuid: projectDetails?.validator?.uuid,
      projectType: projectDetails?.projectType.displayName,
      projectStatus: projectDetails?.status,
      projectTypeUuid: projectDetails?.projectType.uuid,
    },
    locationData: {
      addressCountryCode: projectDetails?.addressCountryCode,
      addressRegion: projectDetails?.addressRegion,
      gridReference: projectDetails?.locationGridReference,
      latitude: projectDetails?.locationCoordinates?.split(",")[0],
      longitude: projectDetails?.locationCoordinates?.split(",")[1],
      locationUuid: projectDetails?.projectLocation?.uuid || null,
      locationRowVersion: projectDetails?.projectLocation?.rowVersion || null,
      boundaryData: projectDetails?.projectLocation?.boundaryData || null,
    },
  });

  const [isReadOnly, setIsReadOnly] = useState(true);

  const [validatorData, setValidatorData] = useState<SelectData>([]);
  const [showUpdateGroupVVBModal, setShowUpdateGroupVVBModal] = useState(false);

  const [showEditDatesPanel, setShowEditDatesPanel] = useState(false);

  const [currentRowVersion, setCurrentRowVersion] = useState<number>(projectDetails?.rowVersion || 1);
  const [errors, setErrors] = useState<ServiceError[]>([]);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (projectDetails?.standardSpecificSchemaUuid) {
        await getStandardSpecificSchemaDetails({
          standardSpecificSchemaUuid: projectDetails.standardSpecificSchemaUuid,
        }).then((response) => {
          if (response.status === Status.Success && response.data) {
            setStandardSpecificDefinition(response.data.definition);
          }
        });
      }
    };
    fetchData();
  }, [projectDetails?.standardSpecificSchemaUuid]);

  useEffect(() => {
    setBasicDetailsData({
      displayName: projectDetails?.displayName,
      name: projectDetails?.name,
      registryId: projectDetails?.projectReference?.externalReference,
      totalArea: projectDetails?.areaNetHa?.toString(),
      validatorUuid: projectDetails?.validator?.uuid,
      projectType: projectDetails?.projectType.displayName,
      projectStatus: projectDetails?.status,
      projectTypeUuid: projectDetails?.projectType.uuid,
    });
    setLocationData({
      addressCountryCode: projectDetails?.addressCountryCode,
      addressRegion: projectDetails?.addressRegion,
      gridReference: projectDetails?.locationGridReference,
      latitude: projectDetails?.locationCoordinates?.split(",")[0] || null,
      longitude: projectDetails?.locationCoordinates?.split(",")[1] || null,
      locationUuid: projectDetails?.projectLocation?.uuid || null,
      locationRowVersion: projectDetails?.projectLocation?.rowVersion || null,
      boundaryData: projectDetails?.projectLocation?.boundaryData || null,
    });
    setCurrentRowVersion(projectDetails?.rowVersion || 1);
  }, [projectDetails]);

  const fetchData = useCallback(async () => {
    const filterCriteria = dataGridMapFilterCriteria([]);

    filterCriteria.projectTypes = {
      some: {
        uuid: {
          operator: "eq",
          value: basicDetailsData.projectTypeUuid,
        },
      },
    };

    await searchVerifiers({
      filter: {
        results: filterCriteria,
      },
      paging: {
        limit: 10,
        beforeCursor: null,
        afterCursor: null,
      },
      sort: [],
    }).then((response) => {
      const valData =
        response.data?.results?.map((x) => {
          return { key: x.uuid, value: x.displayName };
        }) ?? [];
      setValidatorData(valData);
    });
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateProjectDetailsContext = (updatedRowVersion: number): void => {
    if (projectDetails) {
      let locationCoordinates: string | null;

      if (!locationData.latitude && !locationData.longitude) {
        locationCoordinates = null;
      } else {
        locationCoordinates = `${locationData.latitude ? locationData.latitude : ""},${
          locationData.longitude ? locationData.longitude : ""
        }`;
      }

      setProjectDetails({
        ...(projectDetails as GetProjectDetailsResponse),
        areaNetHa: isFalsyExceptZero(basicDetailsData.totalArea) ? null : Number(basicDetailsData.totalArea),
        displayName: basicDetailsData.displayName || "",
        name: basicDetailsData.name || "",
        projectReference: {
          ...projectDetails.projectReference,
          externalReference: basicDetailsData.registryId || "",
        },
        addressCountryCode: locationData.addressCountryCode || null,
        locationCoordinates,
        locationGridReference: locationData.gridReference || null,
        validator: {
          ...projectDetails.validator,
          uuid: basicDetailsData.validatorUuid || "",
        },
        rowVersion: updatedRowVersion,
      });
    }
  };

  const onSubmit = useIsLoadingWrapper(async (e: FormEvent<HTMLFormElement> | undefined): Promise<void> => {
    if (e) e.preventDefault();
    setErrors([]);
    if (projectDetails) {
      if (
        projectDetails.validator?.uuid !== basicDetailsData.validatorUuid &&
        projectDetails.group != null &&
        showUpdateGroupVVBModal === false
      ) {
        setShowUpdateGroupVVBModal(true);
        return;
      }
      let locationCoordinates: string | null;

      if (!locationData.latitude && !locationData.longitude) {
        locationCoordinates = null;
      } else {
        locationCoordinates = `${locationData.latitude ? locationData.latitude : ""},${
          locationData.longitude ? locationData.longitude : ""
        }`;
      }

      const request: UpdateProjectDetailsRequest = {
        rowVersion: currentRowVersion,
        displayName: basicDetailsData.displayName || "",
        projectUuid: projectDetails.uuid,
        areaNetHa: isFalsyExceptZero(basicDetailsData.totalArea) ? null : Number(basicDetailsData.totalArea),
        validatorUuid: basicDetailsData.validatorUuid || null,
        addressCountryCode: locationData.addressCountryCode || null,
        locationCoordinates,
        locationGridReference: locationData.gridReference || "",
        dates: null,
        name: basicDetailsData.name ?? null,
        externalReference: basicDetailsData.registryId ?? null,
        addressRegion: locationData.addressRegion ?? null,
      };

      const updateRes = await updateProjectDetails(request);

      if (updateRes.status === Status.Success && updateRes.data) {
        Toast.success({ message: "Project details changed successfully" });
        setCurrentRowVersion(updateRes.data.rowVersion);
        setErrors([]);
        updateProjectDetailsContext(updateRes.data.rowVersion);
        setSubmittedValues({
          basicDetailsData: basicDetailsData as BasicDetailsData,
          locationData,
        });
        shouldRefreshProjectDetails();
        setShowUpdateGroupVVBModal(false);
        setIsReadOnly(true);
      }

      if (updateRes.status === Status.Error) {
        setErrors(updateRes.errors || []);
        setShowUpdateGroupVVBModal(false);
      }
    }
  }, setIsOnSubmitLoading);

  const onCancel = (): void => {
    setBasicDetailsData(submittedValues.basicDetailsData);
    setLocationData(submittedValues.locationData);
    setIsReadOnly(true);
  };

  // Keep row versions in sync
  useEffect(() => {
    if (projectDates && projectDetails) {
      if (projectDates.rowVersion > projectDetails.rowVersion) {
        setProjectDetails({
          ...projectDetails,
          rowVersion: projectDates?.rowVersion,
        });
      } else if (projectDates.rowVersion < projectDetails.rowVersion) {
        setProjectDates({
          ...projectDates,
          rowVersion: projectDetails?.rowVersion,
        });
      }
    }
  }, [[projectDates?.rowVersion, projectDetails?.rowVersion]]);

  return {
    currentUserType,
    projectDetails,
    projectDates,
    setProjectDates,
    currentStatus: projectDetails?.status,
    isReadOnly,
    setIsReadOnly,
    isEditable: hasProjectPermission(ProjectPermissionConstants.WRITE_PROJECT_DETAILS),
    errors,
    basicDetailsData,
    setBasicDetailsData,
    locationData,
    setLocationData,
    onSubmit,
    onCancel,
    validatorData,
    badgeData: projectDetails?.badges.filter((b) => b.type === BadgeTypeConstants.BADGE_TYPE_BADGE) || [],
    certificateData: projectDetails?.badges.filter((b) => b.type === BadgeTypeConstants.BADGE_TYPE_CERTIFICATION) || [],
    standardSpecificDefinition,
    isOnSubmitLoading,
    showUpdateGroupVVBModal,
    setShowUpdateGroupVVBModal,
    showEditDatesPanel,
    setShowEditDatesPanel,
    shouldRefreshProjectDetails,
  };
};
